import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  SurveyMasterEquipmentSettings,
  MasterEquipmentConfig,
  MasterEquipmentExpansion,
  MasterEquipmentSettingsFileData,
} from '@/types/master-equipment.types'
import * as UnifiedStorage from '@/services/unified-storage.service'
import { useStorageAdapter, type StoreOperationResult } from '@/composables/useStorageAdapter'
import { StorageErrorCode } from '@/services/storage/IStorageAdapter'
import type { IStorageAdapter } from '@/services/storage/IStorageAdapter'
import { calculateEquipmentExpansion } from '@/utils/master-equipment-expansion'
import {
  exportSettingsToFile,
  importSettingsFromFile,
  downloadSettingsAsFile,
} from '@/utils/master-equipment-io'
import { reportError } from '@/lib/error-reporting'

/**
 * Store for managing master equipment expansion settings
 * Handles automatic expansion of master/template equipment based on cable connections
 * Uses unified storage service for automatic persistence (fallback)
 * Supports IStorageAdapter for SQL storage (when injected)
 */
export const useMasterEquipmentStore = defineStore('masterEquipment', () => {
  // Storage adapter composable
  const storage = useStorageAdapter<SurveyMasterEquipmentSettings>()

  // Load from unified storage on initialization (async)
  const loadFromStorage = async (): Promise<Map<string, SurveyMasterEquipmentSettings>> => {
    try {
      const allSurveys = await UnifiedStorage.getAllSurveys()
      const map = new Map<string, SurveyMasterEquipmentSettings>()

      Object.values(allSurveys).forEach(survey => {
        map.set(survey.surveyId, survey.masterEquipment)
      })

      return map
    } catch (error) {
      reportError(error, { where: 'master-equipment.store.loadFromStorage' })
      return new Map()
    }
  }

  // Save to unified storage (async)
  const saveToStorage = async (
    settingsMap: Map<string, SurveyMasterEquipmentSettings>
  ): Promise<void> => {
    try {
      for (const [surveyId, settings] of settingsMap.entries()) {
        await UnifiedStorage.updateSurveyMasterEquipment(surveyId, settings)
      }
    } catch (error) {
      reportError(error, { where: 'master-equipment.store.saveToStorage' })
    }
  }

  // State
  const settings = ref<Map<string, SurveyMasterEquipmentSettings>>(new Map())
  const activeSurveyId = ref<string | null>(UnifiedStorage.getActiveSurveyId())
  const isInitialized = ref(false)
  const isViewingSnapshot = ref(false) // Track when viewing historical version data

  // Initialize async
  loadFromStorage().then(loadedSettings => {
    settings.value = loadedSettings
    isInitialized.value = true
  })

  // Getters
  const activeSettings = computed(() => {
    if (!activeSurveyId.value) return null
    return settings.value.get(activeSurveyId.value) || null
  })

  const hasSettings = computed(() => {
    return activeSurveyId.value && settings.value.has(activeSurveyId.value)
  })

  const configCount = computed(() => {
    if (!activeSettings.value) return 0
    return Object.keys(activeSettings.value.equipmentConfigs).length
  })

  /**
   * Count of equipment that have been expanded (have custom port counts configured)
   * This is used for the badge on the Master Equipment button
   */
  const expandedEquipmentCount = computed(() => {
    if (!activeSettings.value) return 0
    // Count equipment configs that have expansionEnabled or have been explicitly configured
    return Object.values(activeSettings.value.equipmentConfigs).filter(
      config => config.expansionEnabled || config.portsPerInstance > 0
    ).length
  })

  // Actions

  /**
   * Set storage adapter for SQL storage support
   */
  function setStorageAdapter(adapter: IStorageAdapter<SurveyMasterEquipmentSettings>): void {
    storage.setAdapter(adapter)
  }

  /**
   * Load settings from storage adapter
   */
  async function loadSettings(surveyId: string): Promise<boolean> {
    const adapter = storage.adapter()
    if (!adapter) {
      return true // No adapter, use fallback
    }

    const result = await storage.load(surveyId)

    if (!result.success) {
      return false
    }

    if (result.data) {
      settings.value.set(surveyId, result.data.data)
    }

    return true
  }

  /**
   * Get version for a survey (for optimistic locking)
   */
  function getVersion(surveyId: string): number | undefined {
    return storage.getVersion(surveyId)
  }

  /**
   * Register conflict handler
   */
  function onConflict(handler: (_conflict: unknown) => void): () => void {
    return storage.onConflict(handler)
  }

  /**
   * Register auth error handler
   */
  function onAuthError(handler: () => void): () => void {
    return storage.onAuthError(handler)
  }

  async function setActiveSurvey(surveyId: string, surveyName: string): Promise<void> {
    activeSurveyId.value = surveyId

    // If viewing snapshot data, skip loading from storage to preserve restored state
    if (isViewingSnapshot.value && settings.value.has(surveyId)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[MasterEquipmentStore] Viewing snapshot, skipping storage load')
      }
      return
    }

    // Initialize survey in unified storage (fallback)
    const surveyData = await UnifiedStorage.initializeSurvey({ surveyId, surveyName })

    // Load existing settings from SQL storage if adapter is set
    // This also tracks the version for optimistic locking
    if (storage.adapter()) {
      await loadSettings(surveyId)
    }

    // Load existing settings from local storage if not already loaded from SQL
    if (!settings.value.has(surveyId)) {
      settings.value.set(surveyId, surveyData.masterEquipment)
    }
  }

  async function setEquipmentConfig(
    config: MasterEquipmentConfig
  ): Promise<StoreOperationResult<SurveyMasterEquipmentSettings>> {
    if (!activeSurveyId.value) {
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'No active survey',
        },
      }
    }

    const surveySettings = settings.value.get(activeSurveyId.value)
    if (!surveySettings) {
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'Survey settings not found',
        },
      }
    }

    // Save previous state for rollback
    const hadConfig = config.equipmentDefinitionId in surveySettings.equipmentConfigs
    const previousConfig = hadConfig
      ? surveySettings.equipmentConfigs[config.equipmentDefinitionId]
      : undefined
    const previousUpdatedAt = surveySettings.updatedAt

    // Optimistic update
    surveySettings.equipmentConfigs[config.equipmentDefinitionId] = {
      ...config,
      modifiedAt: new Date().toISOString(),
    }
    surveySettings.updatedAt = new Date().toISOString()

    // If storage adapter is set, use it
    if (storage.adapter()) {
      const result = await storage.save(activeSurveyId.value, surveySettings)

      if (!result.success) {
        // Rollback on failure
        if (hadConfig && previousConfig) {
          surveySettings.equipmentConfigs[config.equipmentDefinitionId] = previousConfig
        } else {
          delete surveySettings.equipmentConfigs[config.equipmentDefinitionId]
        }
        surveySettings.updatedAt = previousUpdatedAt
        return result
      }

      return result
    }

    // Fallback to unified storage
    saveToStorage(settings.value)
    return { success: true }
  }

  async function setBulkConfig(
    ids: string[],
    portsPerInstance: number,
    expansionEnabled: boolean
  ): Promise<void> {
    if (!activeSurveyId.value) return
    const surveySettings = settings.value.get(activeSurveyId.value)
    if (!surveySettings) return

    const timestamp = new Date().toISOString()
    ids.forEach(equipmentId => {
      surveySettings.equipmentConfigs[equipmentId] = {
        equipmentDefinitionId: equipmentId,
        equipmentName: equipmentId,
        portsPerInstance,
        expansionEnabled,
        modifiedAt: timestamp,
      }
    })

    surveySettings.updatedAt = timestamp
    await saveToStorage(settings.value)
  }

  async function removeEquipmentConfig(equipmentDefinitionId: string): Promise<void> {
    if (!activeSurveyId.value) return
    const surveySettings = settings.value.get(activeSurveyId.value)
    if (!surveySettings) return

    Reflect.deleteProperty(surveySettings.equipmentConfigs, equipmentDefinitionId)
    surveySettings.updatedAt = new Date().toISOString()
    await saveToStorage(settings.value)
  }

  function getEquipmentConfig(equipmentDefinitionId: string): MasterEquipmentConfig | null {
    if (!activeSurveyId.value) return null
    const surveySettings = settings.value.get(activeSurveyId.value)
    if (!surveySettings) return null
    return (
      (Reflect.get(
        surveySettings.equipmentConfigs,
        equipmentDefinitionId
      ) as MasterEquipmentConfig) || null
    )
  }

  function calculateExpansion(
    spaceEquipmentId: string,
    equipmentDefinitionId: string,
    equipmentName: string,
    totalCables: number
  ): MasterEquipmentExpansion | null {
    return calculateEquipmentExpansion(
      spaceEquipmentId,
      equipmentDefinitionId,
      equipmentName,
      totalCables,
      getEquipmentConfig(equipmentDefinitionId)
    )
  }

  /**
   * Clear all equipment configs for a survey
   * @param surveyId - Survey ID to clear (defaults to active survey)
   * @param skipPersist - If true, only clears local state without persisting (used when data already deleted from SQL)
   */
  async function clearSettings(surveyId?: string, skipPersist = false): Promise<void> {
    const targetId = surveyId || activeSurveyId.value
    if (!targetId) return
    const surveySettings = settings.value.get(targetId)
    if (surveySettings) {
      surveySettings.equipmentConfigs = {}
      surveySettings.updatedAt = new Date().toISOString()
      if (!skipPersist) {
        await saveToStorage(settings.value)
      }
    }
  }

  function exportSettings(surveyId?: string): MasterEquipmentSettingsFileData | null {
    const targetId = surveyId || activeSurveyId.value
    if (!targetId) return null
    const surveySettings = settings.value.get(targetId)
    return surveySettings ? exportSettingsToFile(surveySettings) : null
  }

  async function importSettings(data: MasterEquipmentSettingsFileData): Promise<boolean> {
    const importedSettings = importSettingsFromFile(data)
    if (!importedSettings) return false
    activeSurveyId.value = importedSettings.surveyId
    settings.value.set(importedSettings.surveyId, importedSettings)
    await saveToStorage(settings.value)
    return true
  }

  function downloadSettingsFile(surveyId?: string): void {
    const data = exportSettings(surveyId)
    if (data) downloadSettingsAsFile(data)
  }

  function getStateForSurvey(surveyId: string): SurveyMasterEquipmentSettings | null {
    return settings.value.get(surveyId) || null
  }

  /**
   * Restore state for a survey from a snapshot
   * @param surveyId - The survey ID to restore state for
   * @param state - The state to restore
   * @param skipPersist - If true, only update in-memory state (for version switching)
   */
  async function restoreStateForSurvey(
    surveyId: string,
    state: SurveyMasterEquipmentSettings,
    skipPersist = false
  ): Promise<void> {
    if (!state || state.surveyId !== surveyId) return

    // Mark as viewing snapshot if skipping persist (i.e., loading historical version)
    if (skipPersist) {
      isViewingSnapshot.value = true
    }

    settings.value.set(surveyId, state)

    // Only persist if not restoring from snapshot (version switching)
    if (!skipPersist) {
      await saveToStorage(settings.value)
    }
  }

  /**
   * Clear the snapshot viewing flag.
   * Call this when generating a new report or switching to the latest version
   * to allow loading from storage again.
   */
  function clearSnapshotMode(): void {
    isViewingSnapshot.value = false
  }

  return {
    // State
    settings,
    activeSurveyId,
    isViewingSnapshot,

    // Getters
    activeSettings,
    hasSettings,
    configCount,
    expandedEquipmentCount,

    // Actions
    setStorageAdapter,
    loadSettings,
    getVersion,
    onConflict,
    onAuthError,
    lastError: storage.lastError,
    setActiveSurvey,
    setEquipmentConfig,
    setBulkConfig,
    removeEquipmentConfig,
    getEquipmentConfig,
    calculateExpansion,
    clearSettings,
    exportSettings,
    importSettings,
    downloadSettingsFile,
    getStateForSurvey,
    restoreStateForSurvey,
    clearSnapshotMode,
  }
})
